import { useState, type ReactNode } from "react";
import { useDispatch } from "react-redux";
import { Ban, Check, X } from "lucide-react";
import toast from "react-hot-toast";
import AsyncSelect from "react-select/async";
import ConfirmationModal from "../../../components/Assets/ConfirmationModal";
import { RootDispatch } from "../../../store";
import {
  assignNewStockToFloor,
  getReplaceReturnRequests,
  receiveReplacementFromVendor,
  resolveReplaceReturnRequest,
  reviewReplaceReturnRequest,
  shrink,
  vendorDispatchForReplacement,
} from "../../../store/slices/Factory/materialReturnSlice";
import { fetchVendorsThunk } from "../../../store/slices/warehouseSlice";
import {
  FloorMaterialReturnStatus,
  IFloorMaterialReturnRequest,
} from "../../../models/factory/floor/floorReturnRequest.model";

interface VendorOption {
  value: string;
  label: string;
}

type WorkflowAction =
  | "review"
  | "resolution"
  | "assign"
  | "dispatch"
  | "receive"
  | "shrink";

interface FloorMaterialReturnWorkflowModalProps {
  request: IFloorMaterialReturnRequest;
  page: number;
  statusFilter: string;
  onClose: () => void;
  onRequestUpdated: (request: IFloorMaterialReturnRequest | null) => void;
}

interface WorkflowConfirmation {
  title: string;
  description: string;
  confirmText: string;
  danger: boolean;
  action: () => void;
}

const actionLabels: Record<WorkflowAction, string> = {
  review: "Review request",
  resolution: "Choose resolution",
  assign: "Assign new stock",
  dispatch: "Dispatch to vendor",
  receive: "Receive from vendor",
  shrink: "Record shrink",
};

const inputClass =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-600 focus:ring-2 focus:ring-teal-100";

function getPrimaryAction(
  request: IFloorMaterialReturnRequest,
): WorkflowAction | null {
  if (request.status === "pending_store_review") return "review";
  if (request.status === "accepted" && !request.resolution) return "resolution";
  if (
    request.status === "accepted" &&
    request.resolution &&
    request.resolution !== "return"
  ) {
    return "assign";
  }
  if (
    request.status === "in_progress" &&
    (request.resolution === "replace" || request.resolution === "repair")
  ) {
    const outstanding = request.dispatches.reduce(
      (total, item) =>
        total + Math.max(0, item.quantity - (item.receivedQuantity || 0)),
      0,
    );
    return outstanding > 0 ? "receive" : "dispatch";
  }
  return null;
}

function getAvailableActions(
  request: IFloorMaterialReturnRequest,
): WorkflowAction[] {
  const primaryAction = getPrimaryAction(request);
  const canShrink =
    request.status !== "pending_store_review" &&
    request.status !== "rejected" &&
    request.status !== "cancelled" &&
    request.quantity > (request.shrinkQuantity || 0);
  const actions = primaryAction ? [primaryAction] : [];
  const canAssignStockInProgress =
    request.status === "in_progress" &&
    (request.resolution === "replace" || request.resolution === "repair");
  if (canAssignStockInProgress && !actions.includes("assign")) {
    actions.push("assign");
  }
  if (canShrink && primaryAction !== "shrink") actions.push("shrink");
  return actions;
}

export default function FloorMaterialReturnWorkflowModal({
  request,
  page,
  statusFilter,
  onClose,
  onRequestUpdated,
}: FloorMaterialReturnWorkflowModalProps) {
  const dispatch: RootDispatch = useDispatch();
  const [activeAction, setActiveAction] = useState<WorkflowAction | null>(() =>
    getPrimaryAction(request),
  );
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState<WorkflowConfirmation | null>(
    null,
  );
  const [remark, setRemark] = useState("");
  const [quantity, setQuantity] = useState(String(request.quantity));
  const [vendor, setVendor] = useState<VendorOption | null>(null);
  const [billNumber, setBillNumber] = useState("");
  const [transportCost, setTransportCost] = useState("");
  const [otherCosts, setOtherCosts] = useState("");
  const [repairCost, setRepairCost] = useState("");

  const shrinkRemaining = Math.max(
    0,
    request.quantity - (request.shrinkQuantity || 0),
  );
  const pendingDispatchQuantity = request.dispatches.reduce(
    (total, item) =>
      total + Math.max(0, item.quantity - (item.receivedQuantity || 0)),
    0,
  );
  const availableActions = getAvailableActions(request);
  const isTerminal =
    request.status === "completed" ||
    request.status === "rejected" ||
    request.status === "cancelled";

  const selectAction = (action: WorkflowAction) => {
    setActiveAction(action);
    setRemark("");
    setVendor(null);
    setBillNumber("");
    setTransportCost("");
    setOtherCosts("");
    setRepairCost("");
    if (action === "shrink") {
      setQuantity(String(shrinkRemaining));
    } else if (action === "receive") {
      setQuantity(String(pendingDispatchQuantity));
    } else {
      setQuantity(String(request.quantity));
    }
  };

  const loadVendorOptions = async (search: string): Promise<VendorOption[]> => {
    try {
      const result = await dispatch(
        fetchVendorsThunk({ page: 1, limit: 20, search }),
      ).unwrap();
      return result.data.map((item) => ({
        value: item._id,
        label: item.name,
      }));
    } catch {
      return [];
    }
  };

  const refreshRequest = async () => {
    const result = await dispatch(
      getReplaceReturnRequests({
        page,
        limit: 12,
        ...(statusFilter ? { status: statusFilter } : {}),
      }),
    ).unwrap();
    return (
      result.data?.find(
        (item: IFloorMaterialReturnRequest) => item._id === request._id,
      ) || null
    );
  };

  const runAction = async (
    successMessage: string,
    action: () => Promise<unknown>,
    nextAction?: WorkflowAction | null,
    fallbackRequest?: IFloorMaterialReturnRequest,
  ) => {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      toast.error(
        typeof error === "string" ? error : "Could not complete this action.",
      );
      setBusy(false);
      return;
    }

    toast.success(successMessage);
    try {
      let updatedRequest: IFloorMaterialReturnRequest | null = null;
      try {
        updatedRequest = await refreshRequest();
      } catch {
        toast.error(
          "Action completed, but the request list could not refresh.",
        );
      }

      const currentRequest = updatedRequest ?? fallbackRequest ?? null;
      onRequestUpdated(currentRequest);
      if (currentRequest) {
        setActiveAction(nextAction ?? getPrimaryAction(currentRequest));
        setQuantity(String(currentRequest.quantity));
      } else {
        onClose();
      }
    } finally {
      setBusy(false);
    }
  };

  const requestConfirmation = (
    details: Omit<WorkflowConfirmation, "action">,
    action: () => void,
  ) => setConfirmation({ ...details, action });

  const confirmAction = () => {
    if (!confirmation) return;
    const action = confirmation.action;
    setConfirmation(null);
    action();
  };

  const submitReview = (status: "accepted" | "rejected") =>
    requestConfirmation(
      {
        title: `${status === "accepted" ? "Accept" : "Reject"} this request?`,
        description: `Batch #${request.floorRequestId?.batchNo ?? "N/A"}, ${request.rawMaterialId.name}, quantity ${request.quantity} ${request.rawMaterialId.unit}.${remark.trim() ? ` Note: ${remark.trim()}` : ""}`,
        confirmText:
          status === "accepted" ? "Accept Request" : "Reject Request",
        danger: status === "rejected",
      },
      () =>
        void runAction(`Request ${status}.`, () =>
          dispatch(
            reviewReplaceReturnRequest({
              requestId: request._id,
              status,
              remark: remark.trim() || undefined,
            }),
          ).unwrap(),
        ),
    );

  const submitResolution = (resolution: "return" | "replace" | "repair") =>
    requestConfirmation(
      {
        title: `Set resolution to ${resolution}?`,
        description: `This will set the resolution for ${request.rawMaterialId.name} on batch #${request.floorRequestId?.batchNo ?? "N/A"}.`,
        confirmText: `Set ${resolution}`,
        danger: false,
      },
      () =>
        void runAction(
          `Resolution set to ${resolution}.`,
          () =>
            dispatch(
              resolveReplaceReturnRequest({
                requestId: request._id,
                resolution,
                remark: remark.trim() || undefined,
              }),
            ).unwrap(),
          resolution === "return" ? null : "assign",
          { ...request, resolution },
        ),
    );

  const submitAssignment = () =>
    requestConfirmation(
      {
        title: "Assign new stock to floor?",
        description: `Assign ${quantity} ${request.rawMaterialId.unit} of ${request.rawMaterialId.name} to floor stock.`,
        confirmText: "Assign Stock",
        danger: false,
      },
      () =>
        void runAction("New stock assigned to floor.", () =>
          dispatch(
            assignNewStockToFloor({
              requestId: request._id,
              materialId: String(request.rawMaterialId._id),
              quantity: Number(quantity),
              unit: request.rawMaterialId.unit,
            }),
          ).unwrap(),
        ),
    );

  const submitDispatch = () => {
    if (!vendor) return;
    requestConfirmation(
      {
        title: "Dispatch material to vendor?",
        description: `Send ${quantity} ${request.rawMaterialId.unit} of ${request.rawMaterialId.name} to ${vendor.label}.`,
        confirmText: "Confirm Dispatch",
        danger: false,
      },
      () =>
        void runAction("Vendor dispatch recorded.", () =>
          dispatch(
            vendorDispatchForReplacement({
              requestId: request._id,
              vendorId: vendor.value,
              quantity: Number(quantity),
              trasportCost: Number(transportCost) || 0,
              otherCosts: Number(otherCosts) || 0,
              remark: remark.trim() || undefined,
            }),
          ).unwrap(),
        ),
    );
  };

  const submitReceipt = () => {
    if (!vendor || !billNumber.trim()) return;
    requestConfirmation(
      {
        title: "Record vendor receipt?",
        description: `Receive ${quantity} ${request.rawMaterialId.unit} of ${request.rawMaterialId.name} from ${vendor.label}. Bill number: ${billNumber.trim()}.`,
        confirmText: "Confirm Receipt",
        danger: false,
      },
      () =>
        void runAction("Vendor receipt recorded.", () =>
          dispatch(
            receiveReplacementFromVendor({
              requestId: request._id,
              quantity: Number(quantity),
              unit: request.rawMaterialId.unit,
              vendorId: vendor.value,
              billNumber: billNumber.trim(),
              transportCost: Number(transportCost) || 0,
              otherCosts: Number(otherCosts) || 0,
              repairCost: Number(repairCost) || 0,
              remark: remark.trim() || undefined,
            }),
          ).unwrap(),
        ),
    );
  };

  const submitShrink = () =>
    requestConfirmation(
      {
        title: "Record shrink quantity?",
        description: `Remove ${quantity} ${request.rawMaterialId.unit} of ${request.rawMaterialId.name} from usable stock. This cannot be undone.`,
        confirmText: "Record Shrink",
        danger: true,
      },
      () =>
        void runAction("Shrink quantity recorded.", () =>
          dispatch(
            shrink({
              requestId: request._id,
              quantity: Number(quantity),
              unit: request.rawMaterialId.unit,
            }),
          ).unwrap(),
        ),
    );

  return (
    <>
      <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-[2px]">
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="return-workflow-title"
          className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
        >
          <header className="flex items-start justify-between border-b border-slate-200 border-t-4 border-t-teal-500 bg-white px-5 py-4 sm:px-6">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase text-teal-700">
                Return request workflow
              </p>
              <h2
                id="return-workflow-title"
                className="mt-1 text-xl font-semibold text-slate-900"
              >
                {request.rawMaterialId?.name || "Material request"}
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Batch #{request.floorRequestId?.batchNo ?? "N/A"} ·{" "}
                {request.quantity} {request.rawMaterialId?.unit}
              </p>
            </div>
            <div className="ml-3 flex shrink-0 items-start gap-2">
              <ReturnRequestStatus status={request.status} />
              <button
                type="button"
                aria-label="Close workflow"
                title="Close"
                disabled={busy}
                onClick={onClose}
                className="rounded-md p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </header>

          <div className="overflow-y-auto bg-slate-50 px-5 py-5 sm:px-6">
            {availableActions.length > 0 && (
              <div className="mb-5">
                <p className="mb-2 text-xs font-semibold uppercase text-slate-500">
                  Available actions
                </p>
                <div
                  className="flex flex-wrap gap-2"
                  role="group"
                  aria-label="Available actions"
                >
                  {availableActions.map((action, index) => (
                    <button
                      key={action}
                      type="button"
                      aria-pressed={activeAction === action}
                      onClick={() => selectAction(action)}
                      className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition ${
                        activeAction === action
                          ? "border-teal-700 bg-teal-700 text-white shadow-sm"
                          : "border-slate-300 bg-white text-slate-700 hover:border-teal-500 hover:bg-teal-50"
                      }`}
                    >
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-black/10 text-xs">
                        {index + 1}
                      </span>
                      {actionLabels[action]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {activeAction === "review" && (
              <WorkflowSection title="Review this request">
                <p className="text-sm text-slate-600">
                  Accept the request to choose a resolution, or reject it with
                  an optional note.
                </p>
                <WorkflowField label="Review note (optional)">
                  <textarea
                    value={remark}
                    onChange={(event) => setRemark(event.target.value)}
                    rows={2}
                    className={inputClass}
                    placeholder="Add a note for the requester"
                  />
                </WorkflowField>
                <div className="flex flex-wrap gap-2">
                  <WorkflowButton
                    disabled={busy}
                    onClick={() => submitReview("accepted")}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Check className="h-4 w-4" /> Accept request
                  </WorkflowButton>
                  <WorkflowButton
                    disabled={busy}
                    onClick={() => submitReview("rejected")}
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    <Ban className="h-4 w-4" /> Reject request
                  </WorkflowButton>
                </div>
              </WorkflowSection>
            )}

            {activeAction === "resolution" && (
              <WorkflowSection title="Choose a resolution">
                <p className="text-sm text-slate-600">
                  Select what should happen next for this material.
                </p>
                <div className="grid gap-2 sm:grid-cols-3">
                  {(
                    [
                      ["return", "Return", "Return to store stock"],
                      ["replace", "Replace", "Send replacement"],
                      ["repair", "Repair", "Send for repair"],
                    ] as const
                  ).map(([resolution, label, detail]) => (
                    <button
                      key={resolution}
                      type="button"
                      disabled={busy}
                      onClick={() => submitResolution(resolution)}
                      className="rounded-lg border border-slate-300 bg-white p-3 text-left transition hover:border-teal-500 hover:bg-teal-50 disabled:opacity-50"
                    >
                      <span className="block text-sm font-semibold text-slate-900">
                        {label}
                      </span>
                      <span className="mt-1 block text-xs text-slate-500">
                        {detail}
                      </span>
                    </button>
                  ))}
                </div>
              </WorkflowSection>
            )}

            {activeAction === "assign" && (
              <WorkflowSection title="Assign new stock to floor">
                <p className="text-sm text-slate-600">
                  Resolution: {request.resolution}. Choose the approved quantity
                  to assign.
                </p>
                <WorkflowField
                  label={`Quantity (${request.rawMaterialId.unit})`}
                >
                  <input
                    type="number"
                    min="0.01"
                    max={request.quantity}
                    step="any"
                    value={quantity}
                    onChange={(event) => setQuantity(event.target.value)}
                    className={inputClass}
                  />
                </WorkflowField>
                <WorkflowButton
                  disabled={
                    busy ||
                    !Number(quantity) ||
                    Number(quantity) > request.quantity
                  }
                  onClick={submitAssignment}
                  className="bg-teal-700 hover:bg-teal-800"
                >
                  Assign stock
                </WorkflowButton>
              </WorkflowSection>
            )}

            {(activeAction === "dispatch" || activeAction === "receive") && (
              <WorkflowSection
                title={
                  activeAction === "receive"
                    ? "Receive replacement from vendor"
                    : "Dispatch to vendor"
                }
              >
                <WorkflowField label="Vendor">
                  <AsyncSelect<VendorOption, false>
                    cacheOptions
                    defaultOptions
                    loadOptions={loadVendorOptions}
                    value={vendor}
                    onChange={(option) => setVendor(option)}
                    isClearable
                    placeholder="Search and select a vendor..."
                    className="text-sm"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 130 }),
                    }}
                  />
                </WorkflowField>
                <div className="grid gap-3 sm:grid-cols-2">
                  <WorkflowField
                    label={`Quantity (${request.rawMaterialId.unit})`}
                  >
                    <input
                      type="number"
                      min="0.01"
                      max={
                        activeAction === "receive"
                          ? pendingDispatchQuantity
                          : request.quantity
                      }
                      step="any"
                      value={quantity}
                      onChange={(event) => setQuantity(event.target.value)}
                      className={inputClass}
                    />
                  </WorkflowField>
                  {activeAction === "receive" ? (
                    <WorkflowField label="Bill number">
                      <input
                        value={billNumber}
                        onChange={(event) => setBillNumber(event.target.value)}
                        className={inputClass}
                        required
                      />
                    </WorkflowField>
                  ) : (
                    <WorkflowField label="Transport cost">
                      <input
                        type="number"
                        min="0"
                        value={transportCost}
                        onChange={(event) =>
                          setTransportCost(event.target.value)
                        }
                        className={inputClass}
                      />
                    </WorkflowField>
                  )}
                  {activeAction === "receive" ? (
                    <>
                      <WorkflowField label="Transport cost">
                        <input
                          type="number"
                          min="0"
                          value={transportCost}
                          onChange={(event) =>
                            setTransportCost(event.target.value)
                          }
                          className={inputClass}
                        />
                      </WorkflowField>
                      <WorkflowField label="Other costs">
                        <input
                          type="number"
                          min="0"
                          value={otherCosts}
                          onChange={(event) =>
                            setOtherCosts(event.target.value)
                          }
                          className={inputClass}
                        />
                      </WorkflowField>
                      <WorkflowField label="Repair cost">
                        <input
                          type="number"
                          min="0"
                          value={repairCost}
                          onChange={(event) =>
                            setRepairCost(event.target.value)
                          }
                          className={inputClass}
                        />
                      </WorkflowField>
                    </>
                  ) : (
                    <WorkflowField label="Other costs">
                      <input
                        type="number"
                        min="0"
                        value={otherCosts}
                        onChange={(event) => setOtherCosts(event.target.value)}
                        className={inputClass}
                      />
                    </WorkflowField>
                  )}
                </div>
                <WorkflowField label="Note (optional)">
                  <input
                    value={remark}
                    onChange={(event) => setRemark(event.target.value)}
                    className={inputClass}
                  />
                </WorkflowField>
                <WorkflowButton
                  disabled={
                    busy ||
                    !vendor ||
                    !Number(quantity) ||
                    Number(quantity) >
                      (activeAction === "receive"
                        ? pendingDispatchQuantity
                        : request.quantity) ||
                    (activeAction === "receive" && !billNumber.trim())
                  }
                  onClick={
                    activeAction === "receive" ? submitReceipt : submitDispatch
                  }
                  className="bg-teal-700 hover:bg-teal-800"
                >
                  {activeAction === "receive"
                    ? "Record receipt"
                    : "Dispatch to vendor"}
                </WorkflowButton>
              </WorkflowSection>
            )}

            {activeAction === "shrink" && (
              <WorkflowSection title="Record shrink">
                <p className="text-sm text-slate-600">
                  Record unusable material. Remaining quantity:{" "}
                  {shrinkRemaining} {request.rawMaterialId.unit}.
                </p>
                <WorkflowField
                  label={`Shrink quantity (${request.rawMaterialId.unit})`}
                >
                  <input
                    type="number"
                    min="0.01"
                    max={shrinkRemaining}
                    step="any"
                    value={quantity}
                    onChange={(event) => setQuantity(event.target.value)}
                    className={inputClass}
                  />
                </WorkflowField>
                <WorkflowButton
                  disabled={
                    busy ||
                    !Number(quantity) ||
                    Number(quantity) > shrinkRemaining
                  }
                  onClick={submitShrink}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Record shrink
                </WorkflowButton>
              </WorkflowSection>
            )}

            {!activeAction && isTerminal && (
              <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                {request.status === "completed"
                  ? "This request is complete."
                  : `This request was ${request.status}. No further actions are available.`}
              </p>
            )}
            {!activeAction &&
              !isTerminal &&
              request.resolution === "return" && (
                <p className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                  Return resolution selected. No vendor action is needed.
                </p>
              )}
          </div>

          <footer className="flex items-center justify-between border-t border-slate-200 bg-white px-5 py-3 sm:px-6">
            <span className="text-xs text-slate-500">
              {busy ? "Saving update..." : "Changes apply immediately"}
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={onClose}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Close
            </button>
          </footer>
        </section>
      </div>
      <ConfirmationModal
        isOpen={!!confirmation}
        title={confirmation?.title}
        subTitle={`${request.rawMaterialId.name} · Batch #${request.floorRequestId?.batchNo ?? "N/A"}`}
        description={confirmation?.description}
        confirmText={confirmation?.confirmText}
        cancelText="Review details"
        loading={busy}
        danger={confirmation?.danger ?? false}
        onConfirm={confirmAction}
        onClose={() => setConfirmation(null)}
      />
    </>
  );
}

function ReturnRequestStatus({
  status,
}: {
  status: FloorMaterialReturnStatus;
}) {
  const styles: Record<FloorMaterialReturnStatus, string> = {
    pending_store_review: "border-amber-200 bg-amber-50 text-amber-800",
    rejected: "border-rose-200 bg-rose-50 text-rose-800",
    cancelled: "border-slate-300 bg-slate-100 text-slate-700",
    accepted: "border-sky-200 bg-sky-50 text-sky-800",
    in_progress: "border-indigo-200 bg-indigo-50 text-indigo-800",
    completed: "border-emerald-200 bg-emerald-50 text-emerald-800",
  };

  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${styles[status]}`}
    >
      {status.replaceAll("_", " ")}
    </span>
  );
}

function WorkflowSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      {children}
    </section>
  );
}

function WorkflowField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-1.5 text-xs font-medium text-slate-700">
      <span>{label}</span>
      {children}
    </label>
  );
}

function WorkflowButton({
  children,
  className,
  disabled,
  onClick,
}: {
  children: ReactNode;
  className: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex min-h-10 items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}
